import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  IconButton,
  Checkbox,
  ListItemText
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { toast } from "react-toastify";
import axios from "../../api/axios_client";
import './ManagementPages.css';
import DeleteConfirmationDialog from './DeleteConfirmationDialog';
import { Pagination, Button } from '@mui/material';
import { TextField } from '@mui/material';

const TiffManagement = () => {
  const [tifs, setTiffs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false); // Add delete loading state
  const [selectedIndex, setSelectedIndex] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedDates, setSelectedDates] = useState([]);
  const [indexes, setIndexes] = useState([]);

  // Add new loading state for indexes
  const [indexesLoading, setIndexesLoading] = useState(true);

  // Update fetchIndexes function
  const fetchIndexes = async () => {
    setIndexesLoading(true);
    try {
      const response = await axios.get('/indexes');
      setIndexes(response.data);
    } catch (error) {
      toast.error('Failed to fetch indexes');
    } finally {
      setIndexesLoading(false);
    }
  };

  const fetchTiffs = async () => {
    if (!selectedIndex) {
      setTiffs([]);
      return;
    }

    setLoading(true);
    try {
      const response = await axios.get(`/indexes/${selectedIndex}/tifs`);
      setTiffs(response.data);
      setSelectedDates([]); // Reset selected dates when index changes
    } catch (error) {
      toast.error('Failed to fetch TIFs');
    } finally {
      setLoading(false);
    }
  };
  

  const handleDelete = () => {
    if (selectedDates.length === 0) {
      toast.warn('Please select at least one date to delete');
      return;
    }
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    setDeleteLoading(true); // Start loading
    try {
      await axios.delete(`/indexes/${selectedIndex}/tifs`, {
        data: {
          ids: selectedDates
        }
      });
      
      toast.success('TIFs deleted successfully');
      fetchTiffs();
      setSelectedDates([]);
    } catch (error) {
      // Show backend error message if available, otherwise show generic message
      const errorMessage = error.response?.data?.message || 'Failed to delete TIFs';
      toast.error(errorMessage);
    } finally {
      setDeleteLoading(false); // Stop loading
      setDeleteDialogOpen(false); // Close dialog after loading finishes
    }
  };

  useEffect(() => {
    fetchIndexes();
  }, []);

  useEffect(() => {
    if (selectedIndex) {
      fetchTiffs();
    }
  }, [selectedIndex]);

  const [page, setPage] = useState(1);
  const itemsPerPage = 12;

  // Add new state for date search
  const [searchDate, setSearchDate] = useState('');
  const [searchStartDate, setSearchStartDate] = useState('');
  const [searchEndDate, setSearchEndDate] = useState('');
  const [searchType, setSearchType] = useState('single'); 

  // Add filtered tifs before pagination
  const filteredTiffs = tifs.filter(tif => {
    if (!searchStartDate) return true;
    
    const tifDate = new Date(tif.date);
    if (searchType === 'single') {
      const searchDate = new Date(searchStartDate);
      return tifDate.toISOString().split('T')[0] === searchStartDate;
    } else {
      const start = new Date(searchStartDate);
      const end = new Date(searchEndDate || searchStartDate);
      return tifDate >= start && tifDate <= end;
    }
  });

  // Update pagination to use filtered tifs
  const paginatedTiffs = filteredTiffs.slice(
    (page - 1) * itemsPerPage,
    page * itemsPerPage
  );

  const handleCardClick = (tif) => {
    const idIndex = selectedDates.indexOf(tif.id); 
    if (idIndex > -1) { 
      setSelectedDates(selectedDates.filter(id => id !== tif.id)); 
    } else {
      setSelectedDates([...selectedDates, tif.id]); 
    }
  };

  const handleClearSearch = () => {
    setSearchType('single');
    setSearchStartDate('');
    setSearchEndDate('');
  };
  
  return (
    <div className="management-page">
      <div className="management-header">
        <h1>TIF Management</h1>
        <p>Manage TIF files by index</p>
      </div>

      <div className="filters-section">
    <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap' }}>
    <FormControl fullWidth>
    <InputLabel>Select Index</InputLabel>
    <Select
      value={selectedIndex}
      onChange={(e) => setSelectedIndex(e.target.value)}
      label="Select Index"
    >
      {indexesLoading ? (
        <MenuItem disabled>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CircularProgress size={20} />
            <Typography>Loading indexes...</Typography>
          </Box>
        </MenuItem>
      ) : (
        indexes.map((index) => (
          <MenuItem key={index.id} value={index.id}>
            {index.name}
          </MenuItem>
        ))
      )}
    </Select>
  </FormControl>

      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', minWidth: 200 }}>
    <FormControl>
      <InputLabel>Search Type</InputLabel>
      <Select
        value={searchType}
        onChange={(e) => {
          setSearchType(e.target.value);
          setSearchStartDate('');
          setSearchEndDate('');
        }}
        label="Search Type"
        size="small"
      >
        <MenuItem value="single">Single Date</MenuItem>
        <MenuItem value="range">Date Range</MenuItem>
      </Select>
    </FormControl>

    <FormControl>
      <TextField
        type="date"
        label={searchType === 'single' ? "Date" : "Start Date"}
        value={searchStartDate}
        onChange={(e) => setSearchStartDate(e.target.value)}
        InputLabelProps={{ shrink: true }}
        size="small"
      />
    </FormControl>

    {searchType === 'range' && (
      <FormControl>
        <TextField
          type="date"
          label="End Date"
          value={searchEndDate}
          onChange={(e) => setSearchEndDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          size="small"
        />
      </FormControl>
    )}

    <Button
      variant="outlined"
      onClick={handleClearSearch}
      disabled={!searchStartDate && !searchEndDate}
      size="small"
      sx={{ height: 40 }}
    >
      Clear Search
    </Button>
  </Box>
    </Box>
  </div>

      {loading ? (
        <Box display="flex" justifyContent="center" padding={4}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {tifs.length === 0 ? (
            <Box display="flex" justifyContent="center" padding={4}>
            <Typography variant="h6" color="text.secondary">
              No TIF files available for this index
            </Typography>
          </Box>
          ) : (
            <Box sx={{ mt: 2, mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={handleDelete}
                disabled={selectedDates.length === 0}
                sx={{ mb: 2 }}
              >
                Delete Selected ({selectedDates.length})
              </Button>
            </Box>
          )}

          <div className="items-grid">
            {paginatedTiffs.map((tif) => (
              <div
                key={tif.date}
                className={`item-card ${selectedDates.includes(tif.id) ? 'selected' : ''}`}
                onClick={() => handleCardClick(tif)}
                style={{
                  cursor: 'pointer',
                  border: selectedDates.includes(tif.id) 
                    ? '2px solid #1976d2' 
                    : '1px solid #ddd',
                  backgroundColor: selectedDates.includes(tif.id) 
                    ? '#e3f2fd' 
                    : 'white'
                }}
              >
                <div className="item-header">
                  <Typography variant="h6">
                    Date: {new Date(tif.date).toLocaleDateString()}
                  </Typography>
                </div>
                <div className="item-content">
                  <Typography>Files Count: {tif.count || 1}</Typography>
                </div>
              </div>
            ))}
          </div>

          {tifs.length > itemsPerPage && (
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
              <Pagination
                count={Math.ceil(tifs.length / itemsPerPage)}
                page={page}
                onChange={(e, newPage) => setPage(newPage)}
                color="primary"
              />
            </Box>
          )}
        </>
      )}

      {tifs.length > 0 && (
        <Box sx={{ mt: 2, mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography>
            Showing {filteredTiffs.length} of {tifs.length} TIFs
          </Typography>
          <Button
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={handleDelete}
            disabled={selectedDates.length === 0}
          >
            Delete Selected ({selectedDates.length})
          </Button>
        </Box>
      )}

      <DeleteConfirmationDialog
        isOpen={deleteDialogOpen}
        onClose={() => {
          if (!deleteLoading) { // Only allow closing if not loading
            setDeleteDialogOpen(false);
          }
        }}
        onConfirm={confirmDelete}
        itemName={`${selectedDates.length} selected TIF${selectedDates.length > 1 ? 's' : ''}`}
        isLoading={deleteLoading} // Pass loading state
      />
    </div>
  );
};

export default TiffManagement;